import { useEffect } from 'react';

import * as api from '@/features/backlog/api';
import { ActivityFeedItem } from '@/features/backlog/types';
import { taskPath, whiteboardPath } from '@/constants/routes';
import { highlightTarget } from '@/components/notification-bell';
import { isDesktopApp } from './desktop-updates';

/**
 * Desktop-only: poll the personal activity feed and raise a NATIVE OS notification for new @mentions /
 * assignments. Web-push can't run inside a WebView, so instead the web app (which knows who's mentioned)
 * hands the shell a notification via `window.__sparkDesktop.notify`, and Rust renders it natively — so it
 * shows even when the app window is closed to the tray. A background-quit app can't notify (that needs
 * APNs/WNS); this covers the running/backgrounded case, which is the common one.
 */

const KEY = 'spark-desktop-notify-seen'; // ISO timestamp of the newest feed item already handled
const ENABLED_KEY = 'spark-desktop-notify'; // user toggle (default on)
const MAX_PER_POLL = 5; // avoid a notification storm after a long absence — the rest stay in the bell
const ts = (s: string): number => {
  const t = Date.parse(s);
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Only DIRECTED-at-you events warrant a native OS notification: an @mention, or being freshly assigned
 * an item. Activity on items you merely own / watch / PdM stays in the in-app bell (it's still in the
 * feed) — raising an OS notification for every such change flooded the macOS notify path, where each
 * native notification blocks a thread until clicked (they piled up → runaway CPU/energy). Whiteboard
 * @mentions carry relation 'mention' too, so they're covered.
 */
const isDirectedAtMe = (a: ActivityFeedItem): boolean =>
  a.relation === 'mention' || (a.relation === 'assignee' && a.kind === 'assigned');

/** Whether desktop notifications are enabled (default on). */
export const getDesktopNotifyEnabled = (): boolean => {
  try {
    return localStorage.getItem(ENABLED_KEY) !== '0';
  } catch {
    return true;
  }
};
export const setDesktopNotifyEnabled = (on: boolean): void => {
  try {
    localStorage.setItem(ENABLED_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
};

interface Bridge {
  notify?: (n: { title: string; body: string; url: string }) => void;
}
const bridge = (): Bridge | undefined => (window as { __sparkDesktop?: Bridge }).__sparkDesktop;
const origin = (): string => (typeof location !== 'undefined' ? location.origin : '');

/** Turn a feed item into a native notification. Covers the WHOLE personal feed — @mentions, assignments,
 *  and activity on items you own / watch / PdM — not just mentions. Returns null only if it lacks the
 *  context needed to build a title/link. */
function toNotification(a: ActivityFeedItem): { title: string; body: string; url: string } | null {
  const who = a.actor_name || 'Someone';
  const title = a.whiteboard_id ? a.whiteboard_name || 'Whiteboard' : `${a.item_key} · ${a.item_title}`;
  if (!a.whiteboard_id && !a.item_key) return null;
  // Deep-link to the exact change, matching the in-app bell: the item's own page with ?hl=<target> so the
  // drawer opens the right tab, scrolls to and flashes the changed field/comment (whiteboards use
  // ?comment=<ref_id> to fly to the pinned thread). Without this the tab opened at the item's top and the
  // change wasn't surfaced.
  const target = a.whiteboard_id ? null : highlightTarget(a);
  const url = a.whiteboard_id
    ? origin() + whiteboardPath(a.whiteboard_id) + (a.ref_id ? `?comment=${a.ref_id}` : '')
    : origin() + taskPath(a.board_slug, a.item_key) + (target ? `?hl=${target}` : '');
  const action =
    a.relation === 'mention'
      ? `mentioned you${a.snippet ? `: ${a.snippet}` : ''}`
      : `${a.summary || 'updated this'}${a.detail ? ` ${a.detail}` : ''}${a.snippet && a.kind === 'comment' ? `: ${a.snippet}` : ''}`;
  return { title, body: `${who} ${action}`, url };
}

export function useDesktopNotifications(myId: string | null | undefined): void {
  useEffect(() => {
    if (!isDesktopApp() || !myId) return undefined;
    let stopped = false;
    let baseline = 0;
    try {
      baseline = ts(localStorage.getItem(KEY) || '');
    } catch {
      /* private mode */
    }
    let primed = baseline > 0; // a stored baseline means we can notify immediately
    const seen = new Set<string>();
    const save = (t: number) => {
      baseline = t;
      try {
        localStorage.setItem(KEY, new Date(t || Date.now()).toISOString());
      } catch {
        /* ignore */
      }
    };

    async function poll() {
      if (stopped) return;
      try {
        const feed = await api.myActivityFeed(2, 30); // last 2 days is ample for live notifications
        if (!primed) {
          // First run ever on this device: adopt the newest as the baseline; don't replay the backlog.
          primed = true;
          save(feed.reduce((m, a) => Math.max(m, ts(a.created_at)), Date.now()));
          return;
        }
        const enabled = getDesktopNotifyEnabled();
        const fresh = feed
          .filter((a) => ts(a.created_at) > baseline && a.actor_id !== myId)
          .sort((x, y) => ts(x.created_at) - ts(y.created_at)); // oldest → newest
        let newest = baseline;
        let fired = 0;
        for (const a of fresh) {
          newest = Math.max(newest, ts(a.created_at));
          const key = `${a.item_id}:${a.created_at}:${a.ref_id || ''}`;
          if (seen.has(key)) continue;
          seen.add(key);
          // When the user has notifications off we still advance the baseline (so turning them back on
          // doesn't replay the backlog) — we just don't raise anything.
          if (!enabled) continue;
          // Only raise an OS notification for events aimed AT the user (mention / assignment). The rest
          // of the feed stays in the in-app bell — see isDirectedAtMe for why (macOS notify-thread leak).
          if (!isDirectedAtMe(a)) continue;
          const n = toNotification(a);
          if (n && fired < MAX_PER_POLL) {
            bridge()?.notify?.(n);
            fired += 1;
          }
        }
        if (newest > baseline) save(newest);
      } catch {
        /* offline / transient — retry next tick */
      }
    }

    poll();
    const timer = window.setInterval(poll, 45000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [myId]);
}
